<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import type { UIState } from '@legendary-arena/game-engine';
import SkinSelector from './SkinSelector.vue';
import DangerMeter from './DangerMeter.vue';
import DayNightBadge from './DayNightBadge.vue';
import EndGameControl from './EndGameControl.vue';
import type { SubmitMove } from './uiMoveName.types';

/**
 * Top HUD bar for the WP-129 board layout.
 *
 * Renders `game.{phase, turn, activePlayerId, currentStage}` plus
 * `progress.{bystandersRescued, escapedVillains}` plus scheme/mastermind
 * progress. Mounts the WP-130 `<SkinSelector>` in the slot reserved by
 * D-12907.
 *
 * Per the EC-132 §2 SFC authoring whitelist: this is a tested non-leaf
 * composer with computed state (twist progress, mastermind progress) so
 * it MUST use `defineComponent({ setup() { return {...} } })` per
 * P6-30 / P6-46 / D-6512 (vue-sfc-loader separate-compile rule). The
 * template references non-prop bindings (twist progress text, mastermind
 * progress text, active-player display) that only reach `_ctx` when
 * explicitly returned from setup().
 *
 * @see WP-129 §Acceptance Criteria — Top HUD bar
 * @see DESIGN-BOARD-LAYOUT.md §3.1 Top HUD bar (desktop)
 * @see DESIGN-BOARD-LAYOUT.md §3.2 Sticky Top HUD (mobile)
 * @see DECISIONS.md D-12907 skin-selector slot reserved (WP-130)
 */
export default defineComponent({
  name: 'TopHudBar',
  components: { SkinSelector, DangerMeter, DayNightBadge, EndGameControl },
  props: {
    snapshot: {
      type: Object as PropType<UIState>,
      required: true,
    },
    /**
     * Whether it is the viewing player's turn. Gates the End Game control (only
     * the active player's endMatchEarly applies). Defaults false so an
     * audience / spectator mount never shows it.
     */
    isViewerTurn: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The prop-drilled move dispatcher. Optional: when omitted (e.g. a display-
     * only test mount) the End Game control is not rendered. The pages always
     * pass it so the ribbon carries the escape hatch during live play.
     */
    submitMove: {
      type: Function as PropType<SubmitMove | undefined>,
      required: false,
      default: undefined,
    },
    /**
     * Total Mastermind tactics (e.g., 4 tactics remaining at match start —
     * defeating all of them vanquishes the Mastermind). Owned by the scenario;
     * passed in by the parent which knows the active scenario. NOTE: these are
     * the Mastermind's TACTICS, not its Master Strikes (a separate ability count
     * shown elsewhere) — the HUD labels this counter "Tactics" (WP-618).
     */
    mastermindTacticsTotal: {
      type: Number,
      required: true,
    },
    /**
     * Villain group IDs from the match composition. Optional; when provided,
     * displayed alongside mastermind and scheme.
     */
    villainGroupIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /**
     * Henchman group IDs from the match composition.
     */
    henchmanGroupIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /**
     * Hero deck IDs from the match composition.
     */
    heroDeckIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  setup(props) {
    function activePlayerLabel(): string {
      const id = props.snapshot.game.activePlayerId;
      return id === '' ? 'pending' : id;
    }

    // why: WP-562 / D-24371 §5 — the denominator is back, from
    // `progress.schemeTwistThreshold`. Three readings were rejected on the way
    // here and all three are still wrong: the WP-558-removed hardcoded `/8`
    // (wrong for the unconfigured fallback 7 and for Civil War's 5 at 4-5
    // players), `progress.schemeLossThreshold` (a RESOURCE count for a
    // resourceLossCondition scheme — Negative Zone's is 12 escaped villains, so
    // it renders `Twists: 3/12`), and the bare count WP-558 shipped as the safe
    // interim. `schemeTwistThreshold` is projected separately for exactly this
    // readout and is always the twist-stack size.
    // why: the bare count remains the fallback for a state projecting no
    // threshold — an older fixture or a recorded replay. Never default to a
    // number; a defaulted denominator is the original defect.
    function twistProgressLabel(): string {
      const twistThreshold = props.snapshot.progress.schemeTwistThreshold;
      if (twistThreshold === undefined) {
        return `${props.snapshot.scheme.twistCount}`;
      }
      return `${props.snapshot.scheme.twistCount}/${twistThreshold}`;
    }

    function mastermindProgressLabel(): string {
      const defeated = props.snapshot.mastermind.tacticsDefeated;
      return `${defeated}/${props.mastermindTacticsTotal}`;
    }

    function schemeName(): string {
      const display = props.snapshot.scheme.display;
      if (display !== undefined && display.name !== '<unknown>') {
        return display.name;
      }
      return props.snapshot.scheme.id.replace(/^scheme-/i, '').replace(/-/g, ' ');
    }

    function mastermindName(): string {
      const display = props.snapshot.mastermind.display;
      if (display.name !== '<unknown>') {
        return display.name;
      }
      return props.snapshot.mastermind.id.replace(/-/g, ' ');
    }

    return {
      activePlayerLabel,
      twistProgressLabel,
      mastermindProgressLabel,
      schemeName,
      mastermindName,
    };
  },
});
</script>

<template>
  <header
    class="top-hud-bar"
    data-testid="play-top-hud-bar"
    aria-label="Top HUD"
  >
    <div class="top-hud-bar__row top-hud-bar__setup">
      <span data-testid="play-hud-mastermind-name">
        <strong>Mastermind:</strong> {{ mastermindName() }}
      </span>
      <span data-testid="play-hud-scheme-name">
        <strong>Scheme:</strong> {{ schemeName() }}
      </span>
      <span v-if="villainGroupIds.length > 0" data-testid="play-hud-villain-groups">
        <strong>Villains:</strong> {{ villainGroupIds.join(', ') }}
      </span>
      <span v-if="henchmanGroupIds.length > 0" data-testid="play-hud-henchman-groups">
        <strong>Henchmen:</strong> {{ henchmanGroupIds.join(', ') }}
      </span>
      <span v-if="heroDeckIds.length > 0" data-testid="play-hud-hero-decks">
        <strong>Heroes:</strong> {{ heroDeckIds.join(', ') }}
      </span>
      <span data-testid="play-hud-turn">Turn {{ snapshot.game.turn }}</span>
      <span data-testid="play-hud-active">Active: {{ activePlayerLabel() }}</span>
      <span data-testid="play-hud-stage" class="top-hud-bar__stage">{{ snapshot.game.currentStage }}</span>
      <!-- why (Jeff feedback): the "End Game for everyone" escape hatch lives in
           the top ribbon beside the Mastermind, not under the three turn steps —
           ending the match is a table-level concern, not a turn action. Rendered
           only when submitMove is wired (live play) and gated to the viewer's turn
           inside the control. Pushed to the far right of the Mastermind row. -->
      <EndGameControl
        v-if="submitMove"
        class="top-hud-bar__end-game"
        :is-viewer-turn="isViewerTurn"
        :submit-move="submitMove"
      />
    </div>
    <div class="top-hud-bar__row">
      <span data-testid="play-hud-twists">Twists: {{ twistProgressLabel() }}</span>
      <DangerMeter :progress="snapshot.progress" />
      <!-- why: WP-766 / D-24598 — the engine projects hq.dayNight only when a
           day/night Hero is in the match; an absent field means no badge element
           at all, so every other match's HUD is unchanged. -->
      <DayNightBadge
        v-if="snapshot.hq.dayNight !== undefined"
        :state="snapshot.hq.dayNight"
      />
      <span data-testid="play-hud-tactics">Tactics: {{ mastermindProgressLabel() }}</span>
      <span data-testid="play-hud-bystanders">
        Rescued: {{ snapshot.progress.bystandersRescued }}
      </span>
      <span data-testid="play-hud-escaped">
        Escaped: {{ snapshot.progress.escapedVillains }}
      </span>
      <!-- why: D-12907 reserves the skin-selector slot for WP-130 -->
      <slot name="skin-selector">
        <SkinSelector />
      </slot>
      <!-- why: WP-804 / D-24689 §3 — the public operator-match badge, for every
           seat, so nobody mistakes an unscored operator match for a ranked one.
           In the strip's flow (right end of the short second row) rather than a
           fixed overlay: as a fixed chip below the header it sat on "Mastermind:"
           wherever the board fills the width. -->
      <span
        v-if="snapshot.isDebugMatch === true"
        class="top-hud-bar__operator-badge"
        data-testid="operator-match-badge"
      >Operator match — not scored</span>
    </div>
  </header>
</template>

<style scoped>
.top-hud-bar {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.25rem 0.75rem;
  border-bottom: 1px solid var(--color-foreground, #999);
  font-size: 0.85rem;
}

.top-hud-bar__row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.top-hud-bar__setup {
  font-size: 0.85rem;
  text-transform: capitalize;
}

/* why (Jeff feedback): push the End Game escape hatch to the far right of the
   Mastermind row so it reads as a table-level control, set apart from the
   left-aligned setup data. text-transform:none keeps its label from inheriting
   the row's capitalize. */
.top-hud-bar__end-game {
  margin-inline-start: auto;
  text-transform: none;
}

/* why: pushed to the far right of row 2, mirroring End Game on row 1, so it never
   lengthens the left-aligned counters into a wrap (a wrap would grow the HUD and
   shrink the fitted board). */
.top-hud-bar__operator-badge {
  margin-inline-start: auto;
  padding: 0 0.5rem;
  font-weight: 700;
  color: var(--la-color-gold);
  background: var(--la-color-surface);
  border: 1px solid var(--la-color-border-strong);
  border-radius: 0.4rem;
}

.top-hud-bar__stage {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.top-hud-bar__skin-placeholder {
  opacity: 0.7;
  font-style: italic;
}
</style>
